import { router } from "@inertiajs/react"
import { useEffect, useState } from "react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  adminAccountPath,
  adminAgentArchetypePath,
  adminAgentPath,
  adminPartnerPath,
  adminPartnerUserPath,
  adminUserPath,
} from "@/routes"

interface SearchResult {
  id: string
  record_type: string
  name: string
  email?: string | null
  account_name?: string | null
}

type GroupedResults = Record<string, SearchResult[]>

const RECORD_TYPE_ORDER = [
  "Account",
  "Partner",
  "Client User",
  "Partner User",
  "Custom Agent",
  "Standard Agent",
]

function getPath(result: SearchResult): string | null {
  switch (result.record_type) {
    case "Account":
      return adminAccountPath({ id: result.id })
    case "Client User":
      return adminUserPath({ id: result.id })
    case "Partner User":
      return adminPartnerUserPath({ id: result.id })
    case "Custom Agent":
      return adminAgentPath({ id: result.id })
    case "Standard Agent":
      return adminAgentArchetypePath({ id: result.id })
    case "Partner":
      return adminPartnerPath({ id: result.id })
    default:
      return null
  }
}

export function AdminSearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<GroupedResults>({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset when the parent closes the dialog
      setQuery("")
      setResults({})
      setIsLoading(false)
      return
    }

    const timeoutId = setTimeout(() => {
      if (query.trim().length === 0) {
        setResults({})
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      fetch(`/admin/search_results?q=${encodeURIComponent(query)}`)
        .then((res) => res.json())
        .then((data) => {
          setResults(data.results || {})
          setIsLoading(false)
        })
        .catch(() => {
          setResults({})
          setIsLoading(false)
        })
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [query, open])

  const handleSelect = (result: SearchResult) => {
    const path = getPath(result)
    if (!path) return
    router.visit(path)
    onOpenChange(false)
  }

  const orderedGroups = RECORD_TYPE_ORDER.filter(
    (type) => results[type]?.length > 0,
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-[350px] overflow-hidden p-0"
        showCloseButton={false}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          if (target.closest("[cmdk-item]")) {
            e.preventDefault()
          }
        }}
      >
        <Command className="bg-background rounded-lg border-none">
          <CommandInput
            placeholder="Search for anything"
            value={query}
            onValueChange={setQuery}
            className="focus:border-none focus:shadow-none focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <CommandList className="bg-background">
            {orderedGroups.length === 0 &&
            query.trim().length > 0 &&
            !isLoading ? (
              <CommandEmpty>No results found.</CommandEmpty>
            ) : orderedGroups.length === 0 && query.trim().length === 0 ? (
              <CommandEmpty>Start typing to search...</CommandEmpty>
            ) : orderedGroups.length > 0 ? (
              <>
                {orderedGroups.map((recordType) => (
                  <CommandGroup key={recordType} heading={recordType}>
                    {results[recordType]?.map((result) => {
                      const itemValue = `${result.name} ${result.email || ""} ${result.account_name || ""}`
                      return (
                        <CommandItem
                          key={`${result.record_type}-${result.id}`}
                          value={itemValue}
                          onSelect={() => handleSelect(result)}
                          className="pointer-events-auto cursor-pointer"
                          style={{ pointerEvents: "auto" }}
                        >
                          <div className="flex flex-col">
                            <span className="text-foreground font-semibold">
                              {result.name}
                            </span>
                            {result.email && (
                              <span className="text-muted-foreground text-sm">
                                {result.email}
                              </span>
                            )}
                            {result.account_name && (
                              <span className="text-muted-foreground text-sm">
                                {result.account_name}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                ))}
                {isLoading && (
                  <div className="text-muted-foreground py-2 text-center text-xs">
                    Searching...
                  </div>
                )}
              </>
            ) : isLoading ? (
              <div className="text-muted-foreground py-6 text-center text-sm">
                Searching...
              </div>
            ) : null}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
